(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-popup-offer]';
  const instances = new WeakMap();

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  const setCookie = (name, value, days) => {
    const nextDate = new Date();
    nextDate.setTime(nextDate.getTime() + days * 86400000);
    document.cookie = `${name}=${value}; expires=${nextDate.toUTCString()}; path=/; SameSite=Lax`;
  };

  const hasCookie = (name) => document.cookie.split(';').some((part) => part.trim().startsWith(`${name}=`));

  const getRootUrl = () => {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return '/';
  };

  class PopupOffer {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.cookieName = `popup_offer_v20260420_seen_${this.config.sectionId}`;
      this.state = {
        isOpen: false,
        timerId: null,
        toastId: null,
        totalSeconds: this.getStartingSeconds(),
        maxSeconds: Math.max(1, this.getStartingSeconds())
      };
      this.elements = {
        shell: root.querySelector('[data-popup-shell]'),
        backdrop: root.querySelector('[data-popup-backdrop]'),
        closeButton: root.querySelector('[data-popup-close]'),
        sparkles: root.querySelector('[data-popup-sparkles]'),
        hours: root.querySelector('[data-popup-hours]'),
        minutes: root.querySelector('[data-popup-minutes]'),
        seconds: root.querySelector('[data-popup-seconds]'),
        urgencyFill: root.querySelector('[data-popup-urgency-fill]'),
        urgencyTime: root.querySelector('[data-popup-urgency-time]'),
        toast: root.querySelector('[data-popup-toast]')
      };

      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundExitIntent = this.handleExitIntent.bind(this);

      this.init();
    }

    readConfig() {
      const script = this.root.querySelector('[data-popup-config]');

      if (!script) {
        return {};
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[Popup Offer] Invalid config', error);
        return {};
      }
    }

    getStartingSeconds() {
      return ((Number(this.config.timerHours) || 0) * 3600) + ((Number(this.config.timerMinutes) || 0) * 60) + (Number(this.config.timerSeconds) || 0);
    }

    init() {
      if (!this.elements.shell) {
        return;
      }

      this.bindEvents();
      this.generateSparkles();
      this.startTimer();

      if (window.Shopify && window.Shopify.designMode) {
        this.open(true);
        return;
      }

      if (this.config.exitIntent) {
        document.addEventListener('mouseleave', this.boundExitIntent, { passive: true });
      }

      window.setTimeout(() => {
        if (this.shouldAutoOpen()) {
          this.open();
        }
      }, Number(this.config.showDelay) || 0);
    }

    bindEvents() {
      if (this.elements.closeButton) {
        this.elements.closeButton.addEventListener('click', () => this.close());
      }

      if (this.elements.backdrop) {
        this.elements.backdrop.addEventListener('click', () => {
          if (this.config.closeOnBackdrop) {
            this.close();
          }
        });
      }

      this.root.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-popup-add]');

        if (addButton) {
          event.preventDefault();
          this.handleAddToCart(addButton);
        }
      });
    }

    shouldAutoOpen() {
      return !(this.config.showOnce && hasCookie(this.cookieName));
    }

    rememberOpen() {
      if (this.config.showOnce && !(window.Shopify && window.Shopify.designMode)) {
        setCookie(this.cookieName, '1', Number(this.config.cookieDays) || 1);
      }
    }

    open(forcePreview = false) {
      if (this.state.isOpen) {
        return;
      }

      this.state.isOpen = true;
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');

      if (this.config.lockBodyScroll) {
        document.documentElement.classList.add('popup-offer-lock');
        document.body.classList.add('popup-offer-lock');
      }

      window.requestAnimationFrame(() => {
        this.root.classList.add('is-visible');
      });

      if (!forcePreview) {
        this.rememberOpen();
      }

      document.addEventListener('keydown', this.boundKeydown);
      this.focusFirstElement();
    }

    close() {
      if (!this.state.isOpen) {
        return;
      }

      this.state.isOpen = false;
      this.root.classList.remove('is-visible');

      window.setTimeout(() => {
        this.root.hidden = true;
        this.root.setAttribute('aria-hidden', 'true');
      }, 220);

      document.documentElement.classList.remove('popup-offer-lock');
      document.body.classList.remove('popup-offer-lock');
      document.removeEventListener('keydown', this.boundKeydown);
    }

    focusFirstElement() {
      const focusable = Array.from(this.elements.shell.querySelectorAll(focusableSelector)).filter((node) => !node.hasAttribute('disabled'));
      const target = focusable[0] || this.elements.shell;
      target.focus({ preventScroll: true });
    }

    handleKeydown(event) {
      if (!this.state.isOpen) {
        return;
      }

      if (event.key === 'Escape' && this.config.closeOnEscape) {
        this.close();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = Array.from(this.elements.shell.querySelectorAll(focusableSelector)).filter((node) => !node.hasAttribute('disabled'));

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleExitIntent(event) {
      if (event.clientY > 0 || this.state.isOpen || !this.shouldAutoOpen()) {
        return;
      }

      this.open();
      document.removeEventListener('mouseleave', this.boundExitIntent);
    }

    generateSparkles() {
      if (!this.elements.sparkles || !this.config.showSparkles) {
        return;
      }

      this.elements.sparkles.innerHTML = '';
      const count = Number(this.config.sparkleDensity) || 0;

      for (let index = 0; index < count; index += 1) {
        const sparkle = document.createElement('span');
        const size = 2 + Math.random() * 4;
        sparkle.style.width = `${size}px`;
        sparkle.style.height = `${size}px`;
        sparkle.style.left = `${Math.random() * 100}%`;
        sparkle.style.top = `${14 + Math.random() * 56}%`;
        sparkle.style.animationDelay = `${Math.random() * 2.2}s`;
        sparkle.style.animationDuration = `${1.8 + Math.random() * 1.4}s`;
        this.elements.sparkles.appendChild(sparkle);
      }
    }

    startTimer() {
      this.updateTimer();

      if (!(this.config.showTimer || this.config.showUrgencyBar) || this.state.totalSeconds <= 0) {
        return;
      }

      this.state.timerId = window.setInterval(() => {
        if (this.state.totalSeconds <= 0) {
          window.clearInterval(this.state.timerId);
          this.updateTimer();
          return;
        }

        this.state.totalSeconds -= 1;
        this.updateTimer();
      }, 1000);
    }

    updateTimer() {
      const total = Math.max(0, this.state.totalSeconds);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;

      if (this.elements.hours) {
        this.elements.hours.textContent = pad(hours);
      }

      if (this.elements.minutes) {
        this.elements.minutes.textContent = pad(minutes);
      }

      if (this.elements.seconds) {
        this.elements.seconds.textContent = pad(seconds);
      }

      if (this.elements.urgencyTime) {
        this.elements.urgencyTime.textContent = this.config.urgencyShowSeconds
          ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
          : `${pad(hours)}:${pad(minutes)}`;
      }

      if (this.elements.urgencyFill) {
        const width = Math.max(4, (total / this.state.maxSeconds) * 100);
        this.elements.urgencyFill.style.width = `${width}%`;
      }
    }

    async handleAddToCart(button) {
      const variantId = button.dataset.variant;
      const productUrl = button.dataset.productUrl || '#';

      if (!variantId) {
        if (productUrl && productUrl !== '#') {
          window.location.href = productUrl;
        }
        return;
      }

      const defaultLabel = button.dataset.defaultLabel || 'Add to Cart';
      const addedLabel = button.dataset.addedLabel || this.config.addedToCartText || 'Added!';
      const label = button.querySelector('.po-card__button-label');

      button.disabled = true;
      button.classList.add('is-added');
      if (label) {
        label.textContent = addedLabel;
      }

      try {
        const response = await fetch(this.config.cartUrl || `${getRootUrl()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error('Could not add item to cart');
        }

        const cartResponse = await fetch(this.config.cartStateUrl || `${getRootUrl()}cart.js`, {
          headers: {
            Accept: 'application/json'
          }
        });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        this.updateCartCount(cart);
        this.showToast(addedLabel);
      } catch (error) {
        console.error('[Popup Offer] cart error', error);
        button.classList.remove('is-added');
        this.showToast(this.config.cartErrorText || 'Could not add to cart', true);
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-added');
          if (label) {
            label.textContent = defaultLabel;
          }
        }, 1800);
      }
    }

    updateCartCount(cart) {
      if (!cart || typeof cart.item_count === 'undefined') {
        return;
      }

      const count = String(cart.item_count);
      const selectors = [
        '[data-cart-count]',
        '.cart-count-bubble span:not(.visually-hidden)',
        '#cart-icon-bubble .cart-count-bubble',
        '.header__icon--cart .cart-count-bubble'
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => {
          node.textContent = count;
        });
      });

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    }

    showToast(message, isError = false) {
      if (!this.elements.toast) {
        return;
      }

      window.clearTimeout(this.state.toastId);
      this.elements.toast.textContent = message;
      this.elements.toast.style.background = isError
        ? 'linear-gradient(180deg, #ed5348, #bc2318)'
        : 'linear-gradient(180deg, #1ea862, #137046)';
      this.elements.toast.classList.add('is-visible');

      this.state.toastId = window.setTimeout(() => {
        this.elements.toast.classList.remove('is-visible');
      }, 2200);
    }

    destroy() {
      if (this.state.timerId) {
        window.clearInterval(this.state.timerId);
      }

      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('mouseleave', this.boundExitIntent);
      window.clearTimeout(this.state.toastId);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      instances.set(root, new PopupOffer(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.matches(ROOT_SELECTOR) ? event.target : event.target.querySelector(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    if (instance) {
      instance.open(true);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    if (instance) {
      instance.open(true);
    }
  });
})();
